import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { useRuntimeConfig } from '@/app/runtime-config-context';
import { isRenderableAssetUrl } from '@/app/theme/brand-theme';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BrandLogo } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from './auth-provider';

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const { login, lastSignOut } = useAuth();
  const { brand } = useRuntimeConfig().presentation;
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values.email, values.password);
      onSignedIn();
    } catch (e) {
      if (isApiError(e) && e.code === 'unauthorized') setError('Wrong email or password.');
      else setError(isApiError(e) ? (e.messages[0] ?? 'Sign-in failed.') : 'Sign-in failed.');
    }
  });

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <main className="flex flex-col justify-center gap-8 p-6 md:p-12">
        <div className="mx-auto w-full max-w-sm space-y-8">
          <BrandLogo brand={brand} className="h-9 w-auto" />
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Welcome to {brand.shortName ?? brand.productName}</h1>
            {brand.companyName && <p className="text-sm text-muted-foreground">{brand.companyName}</p>}
          </div>

          {lastSignOut === 'expired' && !error && (
            <Alert>
              <AlertDescription>Your session expired. Please sign in again.</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={(e) => void onSubmit(e)} noValidate>
            <FieldGroup>
              <Field data-invalid={!!errors.email}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  aria-invalid={!!errors.email}
                  {...form.register('email')}
                />
                <FieldError errors={[errors.email]} />
              </Field>
              <Field data-invalid={!!errors.password}>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  {...form.register('password')}
                />
                <FieldError errors={[errors.password]} />
              </Field>
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </FieldGroup>
          </form>
        </div>
      </main>
      {isRenderableAssetUrl(brand.loginHeroUrl) ? (
        <div className="relative hidden bg-muted lg:block">
          <img
            src={brand.loginHeroUrl}
            alt=""
            className="absolute inset-0 size-full object-cover dark:opacity-60 dark:grayscale"
          />
        </div>
      ) : (
        // No hero image in the pack: a quiet brand panel instead of an empty half.
        <div className="relative hidden items-center justify-center overflow-hidden bg-muted lg:flex" aria-hidden>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,color-mix(in_oklab,var(--primary)_8%,transparent),transparent_60%)]" />
          <BrandLogo brand={brand} decorative className="relative w-72 max-w-[60%] opacity-90" />
        </div>
      )}
    </div>
  );
}
